import { Condition, CopyResponse, LocationList, priceFor, ScryfallSearchResponse, type Card, type Copy, type Finish } from '@mtg/shared';
import { useState, type FormEvent } from 'react';
import { Link } from 'wouter';
import { api, errorText } from '../api.ts';
import { CardImage } from '../components/CardImage.tsx';
import { FINISH_LABEL } from '../format.ts';
import { useApi } from '../useApi.ts';

/** Search Scryfall, pick the printing, finish and language, choose where it goes. */
export function AddCardPage() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ScryfallSearchResponse | null>(null);
  const [searching, setSearching] = useState(false);
  const [picked, setPicked] = useState<Card | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [added, setAdded] = useState<Copy | null>(null);

  async function search(e: FormEvent) {
    e.preventDefault();
    setSearching(true);
    setError(null);
    setPicked(null);
    try {
      setResults(await api(`/api/scryfall/search?${new URLSearchParams({ q: query })}`, ScryfallSearchResponse));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setSearching(false);
    }
  }

  return (
    <div className="stack">
      <h1>Add a card</h1>
      <form className="row" onSubmit={search} role="search">
        <input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Card name, or Scryfall syntax like set:mh3 t:land"
          aria-label="Search Scryfall"
          autoFocus
        />
        <button type="submit" className="btn primary" disabled={!query.trim() || searching}>
          {searching ? 'Searching…' : 'Search'}
        </button>
      </form>
      {added && (
        <p className="notice" role="status">
          Added {added.quantity > 1 ? `(now ${added.quantity}) ` : ''}
          <Link href={`/cards/${added.card.scryfallId}`}>{added.card.name}</Link>. <Link href={`/locations/${added.locationId}`}>See where it went</Link>.
        </p>
      )}
      {error && (
        <p className="error" role="alert">
          {error}
        </p>
      )}
      {picked ? (
        <AddForm
          card={picked}
          onCancel={() => setPicked(null)}
          onAdded={(copy) => {
            setAdded(copy);
            setPicked(null);
          }}
        />
      ) : (
        results && (
          <>
            <p className="muted small">
              {results.total === 0
                ? 'No cards found.'
                : `${results.total.toLocaleString()} printing${results.total === 1 ? '' : 's'}${results.hasMore ? ', showing the newest' : ''}. Pick yours:`}
            </p>
            <ul className="card-grid">
              {results.cards.map((card) => (
                <li key={card.scryfallId} className="tile">
                  <button type="button" className="tile-img pick" onClick={() => setPicked(card)} aria-label={`${card.name}, ${card.setName} ${card.collectorNumber}`}>
                    <CardImage card={card} />
                  </button>
                  <div className="tile-meta">
                    <strong>{card.name}</strong>
                    <span className="muted small">
                      {card.setName} · {card.setCode.toUpperCase()} {card.collectorNumber}
                      {card.lang !== 'en' && ` · ${card.lang.toUpperCase()}`}
                    </span>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )
      )}
    </div>
  );
}

function AddForm({ card, onCancel, onAdded }: { card: Card; onCancel: () => void; onAdded: (copy: Copy) => void }) {
  const { data } = useApi(() => api('/api/locations', LocationList), []);
  const intake = data?.locations.find((l) => l.kind === 'intake');
  const [finish, setFinish] = useState<Finish>(card.finishes[0] ?? 'nonfoil');
  const [quantity, setQuantity] = useState(1);
  const [condition, setCondition] = useState<Condition>('NM');
  const [locationId, setLocationId] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const price = priceFor(card, finish);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const { copy } = await api('/api/copies', CopyResponse, {
        body: { scryfallId: card.scryfallId, finish, quantity, condition, locationId: locationId || intake?.id },
      });
      onAdded(copy);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="add-form" onSubmit={submit}>
      <CardImage card={card} />
      <div className="stack">
        <h2>{card.name}</h2>
        <p className="muted">
          {card.setName} · {card.setCode.toUpperCase()} {card.collectorNumber} · {card.lang.toUpperCase()}
        </p>
        <fieldset className="row">
          <legend>Finish</legend>
          {card.finishes.map((f) => (
            <label key={f} className="radio">
              <input type="radio" name="finish" checked={finish === f} onChange={() => setFinish(f)} /> {FINISH_LABEL[f]}
            </label>
          ))}
        </fieldset>
        <div className="row">
          <label className="field">
            <span>Quantity</span>
            <input type="number" min={1} max={999} value={quantity} onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))} />
          </label>
          <label className="field">
            <span>Condition</span>
            <select value={condition} onChange={(e) => setCondition(e.target.value as Condition)}>
              {Condition.options.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </label>
          <label className="field grow">
            <span>Where</span>
            <select value={locationId || intake?.id || ''} onChange={(e) => setLocationId(e.target.value)}>
              {data?.locations.map((l) => (
                <option key={l.id} value={l.id}>
                  {l.name}
                  {l.label ? ` · ${l.label}` : ''}
                </option>
              ))}
            </select>
          </label>
        </div>
        <p className="muted small">{price === null ? 'No TCGplayer price for this finish.' : `$${price.toFixed(2)} each (TCGplayer)`}</p>
        <div className="row">
          <button type="submit" className="btn primary" disabled={busy || !data}>
            {busy ? 'Adding…' : 'Add to collection'}
          </button>
          <button type="button" className="btn" onClick={onCancel}>
            Back to results
          </button>
        </div>
        {error && (
          <p className="error" role="alert">
            {error}
          </p>
        )}
      </div>
    </form>
  );
}
